import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SupportFilters } from '@/components/support/SupportFilters';
import { SupportTicketList } from '@/components/support/SupportTicketList';
import { EmptySupportState } from '@/components/support/EmptySupportState';
import { CreateTicketModal } from '@/components/support/CreateTicketModal';
import { useSupportTickets } from '@/hooks/useSupportTickets';
import UnifiedHeader from '@/components/layout/UnifiedHeader';
import { useCurrentNavItem } from '@/hooks/useCurrentNavItem';

const Support = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const currentNavItem = useCurrentNavItem();
  const Icon = currentNavItem?.icon || Headphones;

  const { data: tickets, isLoading } = useSupportTickets(statusFilter);

  const handleTicketClick = (ticketId: string) => {
    navigate(`/main/support/${ticketId}`);
  };

  const handleCreateSuccess = (ticketId: string) => {
    navigate(`/main/support/${ticketId}`);
  };

  const showEmptyState = !isLoading && (!tickets || tickets.length === 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 pb-20">
      {/* Header */}
      <UnifiedHeader
        title={currentNavItem?.label || "Suporte"}
        subtitle={tickets ? `${tickets.length} tickets` : currentNavItem?.description}
        icon={Icon}
        iconColor="text-white"
        iconBgColor="bg-luxury-pinkBright border-luxury-pinkBright"
      />

      {/* Filtros fora do header */}
      <div className="pt-20 sm:pt-24 px-4 max-w-7xl mx-auto">
        <SupportFilters value={statusFilter} onChange={setStatusFilter} />
      </div>

      <main className="container max-w-7xl mx-auto pt-4 px-[16px]">
        {/* Content */}
        {showEmptyState ? (
          <EmptySupportState onCreateTicket={() => setCreateModalOpen(true)} />
        ) : (
          <SupportTicketList
            tickets={tickets}
            isLoading={isLoading}
            onTicketClick={handleTicketClick}
          />
        )}
      </main>

      {/* FAB - Novo Ticket (only show when there are tickets) */}
      {!showEmptyState && (
        <Button
          onClick={() => setCreateModalOpen(true)}
          size="lg"
          className="fixed bottom-20 md:bottom-8 right-4 h-14 w-14 rounded-full shadow-lg bg-luxury-pinkBright hover:bg-luxury-pinkBright/90 border border-white/10"
        >
          <Plus className="w-6 h-6" />
        </Button>
      )}

      {/* Create Modal */}
      <CreateTicketModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Support;
